import React, { useState, useEffect } from "react";
import {
  PostsContainer,
  PostsGrid,
  PostCard,
  PostImage,
  PostContent,
  PostTitle,
  PostDescription,
  PostDate,
  PostButton,
  LoadingContainer,
  LoadingSpinner,
  ErrorContainer,
  ErrorBox,
  PaginationContainer,
  PaginationButton,
  PageNumbersContainer,
} from "./styled";

import { colors } from "../../../theme/colors";

import { SvgIcon } from "@mui/material";

import { ReactComponent as AtrasIcon } from "../../../img/svg/bi-icons-ir-izquierda.svg";

import { ReactComponent as AdelanteIcon } from "../../../img/svg/bi-icons-ir-derecha.svg";

const WordPressPosts = ({
  baseUrl = "https://www.corporacionbi.com/gt/bancoindustrial",
  postType = "blog-bi",
}) => {
  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const categoryId = 449;

  const postsPerPage = 6;

  useEffect(() => {
    fetchPosts(currentPage);
  }, [currentPage, baseUrl, postType]);

  const fetchPosts = async (page) => {
    try {
      setLoading(true);

      let url = `${baseUrl}/wp-json/wp/v2/${postType}?per_page=${postsPerPage}&page=${page}&_embed&categories=${categoryId}`;
      console.log("Intentando URL con categoría:", url);

      let response = await fetch(url);

      if (!response.ok && postType !== "posts") {
        console.log("Fallback a posts estándar con categoría");
        url = `${baseUrl}/wp-json/wp/v2/posts?per_page=${postsPerPage}&page=${page}&_embed&categories=${categoryId}`;
        response = await fetch(url);
      }

      if (!response.ok) {
        throw new Error(
          `Error: ${response.status} - No se pudieron cargar los posts de la categoría`
        );
      }

      const data = await response.json();
      const totalPosts = parseInt(response.headers.get("X-WP-Total"));
      const totalPagesCount = Math.ceil(totalPosts / postsPerPage);

      console.log("Posts encontrados con categoría:", data.length);
      console.log("Total posts:", totalPosts);

      setPosts(data);
      setTotalPages(totalPagesCount);
      setError(null);
    } catch (err) {
      console.error("Error detallado:", err);
      setError(err.message);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const getPostImage = (post) => {
    if (post._embedded && post._embedded["wp:featuredmedia"]) {
      return post._embedded["wp:featuredmedia"][0].source_url;
    }
    return "https://via.placeholder.com/400x250/e2e8f0/64748b?text=Sin+Imagen";
  };

  const stripHtml = (html) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  const truncateText = (text, maxLength = 120) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const generatePaginationNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        startPage = 2;
        endPage = Math.min(4, totalPages - 1);
      } else if (currentPage >= totalPages - 2) {
        startPage = Math.max(totalPages - 3, 2);
        endPage = totalPages - 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handleRetry = () => {
    setError(null);
    fetchPosts(currentPage);
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorBox>
          <p>Error al cargar los posts: {error}</p>
          <button
            onClick={handleRetry}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: colors.secondary,
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Reintentar
          </button>
        </ErrorBox>
      </ErrorContainer>
    );
  }

  return (
    <PostsContainer>
      <PostsGrid>
        {posts.map((post) => (
          <PostCard key={post.id}>
            <PostImage>
              <img
                src={getPostImage(post)}
                alt={stripHtml(post.title.rendered)}
                loading="lazy"
              />
            </PostImage>

            <PostContent>
              <PostTitle>{stripHtml(post.title.rendered)}</PostTitle>

              <PostDescription>
                {truncateText(stripHtml(post.excerpt.rendered))}
              </PostDescription>

              <PostDate>
                {new Date(post.date).toLocaleDateString("es-ES", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </PostDate>

              <PostButton onClick={() => window.open(post.link, "_blank")}>
                Leer más
              </PostButton>
            </PostContent>
          </PostCard>
        ))}
      </PostsGrid>

      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={currentPage === 1 ? "disabled" : ""}
          >
            <SvgIcon
              component={AtrasIcon}
              inheritViewBox
              sx={{
                fontSize: "2rem",
                "& path": {
                  fill: colors.secondary,
                },
              }}
            />
          </PaginationButton>

          <PageNumbersContainer>
            {generatePaginationNumbers().map((pageNum, index) => {
              if (pageNum === "...") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    style={{
                      padding: "0.75rem 0.5rem",
                      color: colors.secondary,
                      fontWeight: "700",
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    ...
                  </span>
                );
              }

              return (
                <PaginationButton
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={pageNum === currentPage ? "active" : ""}
                >
                  {pageNum}
                </PaginationButton>
              );
            })}
          </PageNumbersContainer>

          <PaginationButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages <= 1}
            className={
              currentPage === totalPages || totalPages <= 1 ? "disabled" : ""
            }
          >
            <SvgIcon
              component={AdelanteIcon}
              inheritViewBox
              sx={{
                fontSize: "2rem",
                "& path": {
                  fill: colors.secondary,
                },
              }}
            />
          </PaginationButton>
        </PaginationContainer>
      )}
    </PostsContainer>
  );
};

export default WordPressPosts;
